import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    Users, Upload, Search, Loader2, FileText, CalendarRange, ClipboardCheck, Lock, KeyRound, X, Mail, Info,
} from "lucide-react";
import api from "../../api/api";
import {
    Alert, EmptyState, Field, OrgModal, Panel, Pill, apiError, formatDate, useOrgStyles,
} from "../../components/org/orgUi";
import { useOrgPortal } from "./orgPortalContext";

export default function OrgStudents() {
    const { orgId, org, isOrgAdmin, reloadOrg } = useOrgPortal();
    const s = useOrgStyles();
    const [searchParams, setSearchParams] = useSearchParams();
    const [students, setStudents] = useState([]);
    const [programs, setPrograms] = useState([]);
    const [invites, setInvites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");
    const [debounced, setDebounced] = useState("");
    const [importOpen, setImportOpen] = useState(false);
    const programId = searchParams.get("programId") || "";
    const sort = searchParams.get("sort") === "program" ? "program" : "alpha";
    const openStudentId = Number(searchParams.get("student")) || null;

    const setParam = (key, value) => {
        const next = new URLSearchParams(searchParams);
        if (value) next.set(key, value); else next.delete(key);
        setSearchParams(next, { replace: true });
    };

    useEffect(() => {
        const t = setTimeout(() => setDebounced(search.trim()), 250);
        return () => clearTimeout(t);
    }, [search]);

    const load = useCallback(async () => {
        try {
            const params = new URLSearchParams({ sort });
            if (programId) params.set("programId", programId);
            if (debounced) params.set("search", debounced);
            const res = await api.get(`/org/${orgId}/roster?${params}`);
            setStudents(res.data.students || []);
        } catch (err) {
            setError(apiError(err, "Couldn't load students."));
        } finally {
            setLoading(false);
        }
    }, [orgId, programId, sort, debounced]);

    const loadInvites = useCallback(async () => {
        if (!isOrgAdmin) return;
        try {
            const res = await api.get(`/org/${orgId}/invites`);
            setInvites(res.data.invites || []);
        } catch { setInvites([]); }
    }, [orgId, isOrgAdmin]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        api.get(`/org/${orgId}/programs`).then((res) => setPrograms(res.data.programs || [])).catch(() => setPrograms([]));
        loadInvites();
    }, [orgId, loadInvites]);

    const cancelInvite = async (invite) => {
        try {
            await api.delete(`/org/${orgId}/invites/${invite.InviteID}`);
            loadInvites();
        } catch (err) { setError(apiError(err)); }
    };

    const refreshAll = async () => { await Promise.all([load(), loadInvites(), reloadOrg()]); };

    return (
        <div className="space-y-6">
            <Alert onClose={() => setError("")}>{error}</Alert>
            <Panel
                title="Student roster"
                padded={false}
                actions={isOrgAdmin && (
                    <button type="button" className={s.btnPrimary} onClick={() => setImportOpen(true)}><Upload size={16} /> Import students</button>
                )}
            >
                <div className={`flex flex-col gap-3 border-b px-5 py-4 md:flex-row md:items-center ${s.dark ? "border-slate-700" : "border-slate-100"}`}>
                    <div className="relative flex-1">
                        <Search size={16} className={`pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 ${s.muted}`} />
                        <input
                            aria-label="Search students"
                            placeholder="Search by name or email"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className={`${s.input} pl-9`}
                        />
                    </div>
                    <select aria-label="Filter by program" value={programId} onChange={(e) => setParam("programId", e.target.value)} className={`${s.input} md:w-56`}>
                        <option value="">All programs</option>
                        {programs.map((p) => <option key={p.ProgramID} value={p.ProgramID}>{p.ProgramName}</option>)}
                    </select>
                    <select aria-label="Sort" value={sort} onChange={(e) => setParam("sort", e.target.value === "alpha" ? "" : e.target.value)} className={`${s.input} md:w-48`}>
                        <option value="alpha">Sort: A–Z</option>
                        <option value="program">Sort: by program</option>
                    </select>
                </div>
                {loading ? (
                    <div className="flex justify-center py-10"><Loader2 className={`animate-spin ${s.muted}`} /></div>
                ) : students.length === 0 ? (
                    <EmptyState
                        icon={Users}
                        title={debounced || programId ? "No matching students" : "No students yet"}
                        body={debounced || programId ? "Try a different search or program." : "Share a program's invitation code, or import students by email."}
                    />
                ) : (
                    <RosterTable students={students} sort={sort} onOpen={(id) => setParam("student", id)} />
                )}
            </Panel>

            {isOrgAdmin && invites.length > 0 && (
                <Panel title={`Pending invites (${invites.length})`} padded={false}>
                    <p className={`px-5 pt-4 text-sm ${s.muted}`}>
                        These emails don't have a Build My Biz Brand account yet. They join automatically when they sign up and sign in with that email.
                    </p>
                    <ul className={`divide-y px-5 ${s.divider}`}>
                        {invites.map((i) => (
                            <li key={i.InviteID} className="flex items-center justify-between gap-3 py-3 text-sm">
                                <span className={`inline-flex items-center gap-2 ${s.body}`}><Mail size={14} className={s.muted} /> {i.Email}</span>
                                <span className="flex items-center gap-3">
                                    {i.ProgramName && <Pill>{i.ProgramName}</Pill>}
                                    <button type="button" className={s.btnDanger} onClick={() => cancelInvite(i)}>Cancel</button>
                                </span>
                            </li>
                        ))}
                    </ul>
                </Panel>
            )}

            {importOpen && (
                <ImportModal orgId={orgId} org={org} programs={programs} defaultProgramId={programId}
                             onClose={() => setImportOpen(false)} onDone={refreshAll} />
            )}
            {openStudentId && (
                <StudentDetail orgId={orgId} clientId={openStudentId} isOrgAdmin={isOrgAdmin} programs={programs}
                               onClose={() => setParam("student", "")} onChanged={refreshAll} />
            )}
        </div>
    );
}

function SharedIcons({ student }) {
    const s = useOrgStyles();
    const items = [
        { on: student.ShareResume, icon: FileText, label: "Resume" },
        { on: student.ShareQuarterlyResults, icon: CalendarRange, label: "Quarterly results" },
        { on: student.ShareProgramProgress, icon: ClipboardCheck, label: "Program progress" },
    ];
    return (
        <span className="inline-flex items-center gap-1.5">
            {items.map(({ on, icon: Icon, label }) => (
                <span key={label} title={`${label}: ${on ? "shared" : "not shared"}`} aria-label={`${label}: ${on ? "shared" : "not shared"}`}>
                    <Icon size={15} className={on ? (s.dark ? "text-primary-400" : "text-primary-600") : (s.dark ? "text-slate-600" : "text-slate-300")} />
                </span>
            ))}
        </span>
    );
}

function RosterTable({ students, sort, onOpen }) {
    const s = useOrgStyles();
    return (
        <div className="overflow-x-auto">
            <table className="w-full">
                <thead>
                    <tr className={`border-b ${s.dark ? "border-slate-700" : "border-slate-100"}`}>
                        <th className={s.th}>Student</th>
                        <th className={s.th}>{sort === "program" ? "Program" : "Programs"}</th>
                        <th className={s.th}>License</th>
                        <th className={s.th}>Shared</th>
                        <th className={s.th}><span className="sr-only">Open</span></th>
                    </tr>
                </thead>
                <tbody>
                    {students.map((st, i) => (
                        <tr key={`${st.ClientID}-${st.ProgramID || i}`} className={`border-b last:border-0 ${s.row}`}>
                            <td className={s.td}>
                                <button type="button" onClick={() => onOpen(st.ClientID)} className={`text-left font-semibold ${s.text} hover:underline`}>
                                    {st.LastName}, {st.FirstName}
                                </button>
                                <div className={`text-xs ${s.muted}`}>{st.EmailAddress}</div>
                            </td>
                            <td className={s.td}>
                                {sort === "program"
                                    ? (st.ProgramName || <span className={s.muted}>No program</span>)
                                    : (st.Programs || []).length
                                        ? <span className="flex flex-wrap gap-1">{st.Programs.map((p) => <Pill key={p.ProgramID}>{p.ProgramName}</Pill>)}</span>
                                        : <span className={s.muted}>No program</span>}
                            </td>
                            <td className={s.td}>{st.HasLicense ? <Pill tone="success">Licensed</Pill> : <span className={s.muted}>None</span>}</td>
                            <td className={s.td}><SharedIcons student={st} /></td>
                            <td className={`${s.td} text-right`}>
                                <button type="button" className={s.btnLink} onClick={() => onOpen(st.ClientID)}>View</button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function ImportModal({ orgId, org, programs, defaultProgramId, onClose, onDone }) {
    const s = useOrgStyles();
    const [emails, setEmails] = useState("");
    const [programId, setProgramId] = useState(defaultProgramId || "");
    const [assignLicense, setAssignLicense] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [result, setResult] = useState(null);
    const count = useMemo(() => new Set(emails.split(/[\s,;]+/).map((e) => e.trim().toLowerCase()).filter(Boolean)).size, [emails]);

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const text = await file.text();
        const found = text.match(/[^\s@,;"'<>]+@[^\s@,;"'<>]+\.[^\s@,;"'<>]+/g) || [];
        setEmails((prev) => [prev.trim(), ...found].filter(Boolean).join("\n"));
        e.target.value = "";
    };

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        try {
            const res = await api.post(`/org/${orgId}/students/import`, {
                emails, programId: programId ? Number(programId) : null, assignLicense,
            });
            setResult(res.data);
            await onDone();
        } catch (err) {
            setError(apiError(err));
        } finally {
            setBusy(false);
        }
    };

    const seatsLeft = Math.max(0, org.StudentLicenseLimit - org.StudentLicensesUsed);

    return (
        <OrgModal
            isOpen
            wide
            onClose={onClose}
            title="Import students"
            footer={result
                ? <button type="button" className={s.btnPrimary} onClick={onClose}>Done</button>
                : <>
                    <button type="button" className={s.btnSecondary} onClick={onClose}>Cancel</button>
                    <button type="submit" form="import-form" className={s.btnPrimary} disabled={busy || count === 0}>
                        {busy && <Loader2 size={14} className="animate-spin" />} Import {count || ""} email{count === 1 ? "" : "s"}
                    </button>
                </>}
        >
            <Alert>{error}</Alert>
            {result ? (
                <div className="space-y-3 text-sm">
                    <ResultLine label="Added to your roster" items={result.added} tone="success" />
                    <ResultLine label="Already on your roster" items={result.alreadyMember} />
                    <ResultLine label="Invited (no account yet; they join when they sign up)" items={result.invited} tone="primary" />
                    <ResultLine label="Licenses assigned" items={result.licensed} tone="success" />
                    <ResultLine label="Not valid email addresses" items={result.invalid} tone="danger" />
                    {result.licenseErrors?.length > 0 && (
                        <div>
                            <div className={`font-semibold ${s.text}`}>Licenses not assigned ({result.licenseErrors.length})</div>
                            <p className={s.muted}>{result.licenseErrors[0].error}</p>
                        </div>
                    )}
                </div>
            ) : (
                <form id="import-form" onSubmit={submit} className="space-y-4">
                    <Field label="Emails" htmlFor="imp-emails" hint="One per line, or separated by commas. You can also load a CSV file.">
                        <textarea id="imp-emails" rows={8} className={`${s.input} font-mono`} value={emails}
                                  onChange={(e) => setEmails(e.target.value)} placeholder={"student1@school.edu\nstudent2@school.edu"} />
                    </Field>
                    <label className={`inline-flex cursor-pointer items-center gap-2 ${s.btnSecondary}`}>
                        <Upload size={14} /> Load from CSV
                        <input type="file" accept=".csv,.txt,text/csv,text/plain" className="sr-only" onChange={onFile} />
                    </label>
                    <Field label="Add to program" htmlFor="imp-program">
                        <select id="imp-program" className={s.input} value={programId} onChange={(e) => setProgramId(e.target.value)}>
                            <option value="">Roster only (no program)</option>
                            {programs.map((p) => <option key={p.ProgramID} value={p.ProgramID}>{p.ProgramName}</option>)}
                        </select>
                    </Field>
                    {org.StudentLicenseLimit > 0 && (
                        <label className={`flex items-start gap-2 text-sm ${s.body}`}>
                            <input type="checkbox" className="mt-1" checked={assignLicense} onChange={(e) => setAssignLicense(e.target.checked)} />
                            <span>
                                Assign a student license to each existing account
                                <span className={`block text-xs ${s.muted}`}>{seatsLeft} of {org.StudentLicenseLimit} student licenses left this month. Invited emails get a license once they join.</span>
                            </span>
                        </label>
                    )}
                </form>
            )}
        </OrgModal>
    );
}

function ResultLine({ label, items, tone = "neutral" }) {
    const s = useOrgStyles();
    if (!items?.length) return null;
    return (
        <div>
            <div className={`mb-1 flex items-center gap-2 font-semibold ${s.text}`}>{label} <Pill tone={tone}>{items.length}</Pill></div>
            <p className={`break-words ${s.muted}`}>{items.slice(0, 12).join(", ")}{items.length > 12 ? ` and ${items.length - 12} more` : ""}</p>
        </div>
    );
}

function StudentDetail({ orgId, clientId, isOrgAdmin, programs, onClose, onChanged }) {
    const s = useOrgStyles();
    const [data, setData] = useState(null);
    const [license, setLicense] = useState(null);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [addProgramId, setAddProgramId] = useState("");
    const [analysis, setAnalysis] = useState(null);

    const load = useCallback(async () => {
        try {
            const res = await api.get(`/org/${orgId}/students/${clientId}`);
            setData(res.data);
            if (isOrgAdmin) {
                const lic = await api.get(`/org/${orgId}/licenses`);
                setLicense((lic.data.licenses || []).find((l) => l.ClientID === clientId) || null);
            }
        } catch (err) {
            setError(apiError(err, "Couldn't load this student."));
        }
    }, [orgId, clientId, isOrgAdmin]);

    useEffect(() => { load(); }, [load]);

    const run = async (fn) => {
        setBusy(true);
        setError("");
        try {
            await fn();
            await Promise.all([load(), onChanged()]);
        } catch (err) {
            setError(apiError(err));
        } finally {
            setBusy(false);
        }
    };

    const openAnalysis = async (id) => {
        try {
            const res = await api.get(`/org/${orgId}/students/${clientId}/resume-analyses/${id}`);
            const a = res.data.analysis;
            let results = null;
            try { results = JSON.parse(a.ResultsJSON || "null"); } catch { results = null; }
            setAnalysis({ ...a, results });
        } catch (err) {
            setError(apiError(err));
        }
    };

    const student = data?.student;
    const enrolledIds = new Set((data?.programs || []).map((p) => p.ProgramID));
    const available = programs.filter((p) => !enrolledIds.has(p.ProgramID));

    return (
        <OrgModal isOpen wide onClose={onClose} title={student ? `${student.FirstName} ${student.LastName}` : "Student"}>
            <Alert onClose={() => setError("")}>{error}</Alert>
            {!data ? (
                !error && <div className="flex justify-center py-10"><Loader2 className={`animate-spin ${s.muted}`} /></div>
            ) : analysis ? (
                <AnalysisView analysis={analysis} onBack={() => setAnalysis(null)} />
            ) : (
                <div className="space-y-6">
                    <div className={`flex flex-wrap items-center justify-between gap-2 text-sm ${s.body}`}>
                        <span>{student.EmailAddress}</span>
                        <span className={s.muted}>Joined {formatDate(student.JoinedOn)}</span>
                    </div>

                    <section>
                        <h4 className={`mb-2 text-sm font-bold ${s.text}`}>Programs</h4>
                        {data.programs.length === 0 ? <p className={`text-sm ${s.muted}`}>Not in a program.</p> : (
                            <ul className="flex flex-wrap gap-2">
                                {data.programs.map((p) => (
                                    <li key={p.ProgramID} className={`inline-flex items-center gap-1 rounded-full border py-1 pl-3 pr-1 text-sm ${s.subPanel} ${s.body}`}>
                                        {p.ProgramName}
                                        {isOrgAdmin && (
                                            <button type="button" disabled={busy} aria-label={`Remove from ${p.ProgramName}`}
                                                    onClick={() => run(() => api.delete(`/org/${orgId}/students/${clientId}/programs/${p.ProgramID}`))}
                                                    className={`rounded-full p-1 ${s.dark ? "hover:bg-slate-700" : "hover:bg-slate-200"}`}>
                                                <X size={12} />
                                            </button>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                        {isOrgAdmin && available.length > 0 && (
                            <div className="mt-3 flex gap-2">
                                <select aria-label="Add to program" className={`${s.input} max-w-xs`} value={addProgramId} onChange={(e) => setAddProgramId(e.target.value)}>
                                    <option value="">Add to a program…</option>
                                    {available.map((p) => <option key={p.ProgramID} value={p.ProgramID}>{p.ProgramName}</option>)}
                                </select>
                                <button type="button" className={s.btnSecondary} disabled={!addProgramId || busy}
                                        onClick={() => run(async () => {
                                            await api.post(`/org/${orgId}/students/${clientId}/programs`, { programId: Number(addProgramId) });
                                            setAddProgramId("");
                                        })}>Add</button>
                            </div>
                        )}
                    </section>

                    {isOrgAdmin && (
                        <section className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border p-4 ${s.subPanel}`}>
                            <div className="flex items-center gap-2 text-sm">
                                <KeyRound size={16} className={s.muted} />
                                {license?.IsActive
                                    ? <span className={s.body}>Student license active this month.</span>
                                    : license
                                        ? <span className={s.body}>License removed this month (the seat frees up on the 1st).</span>
                                        : <span className={s.body}>No student license.</span>}
                            </div>
                            {license?.IsActive ? (
                                <button type="button" className={s.btnDanger} disabled={busy}
                                        onClick={() => window.confirm("Remove this license? The student loses access now, and the seat can't be reassigned until next month.")
                                            && run(() => api.delete(`/org/${orgId}/licenses/${license.LicenseID}`))}>
                                    Remove license
                                </button>
                            ) : (
                                <button type="button" className={s.btnSecondary} disabled={busy}
                                        onClick={() => run(() => api.post(`/org/${orgId}/licenses`, { licenseType: "student", clientId }))}>
                                    Assign license
                                </button>
                            )}
                        </section>
                    )}

                    <SharedSections data={data} onOpenAnalysis={openAnalysis} />

                    {isOrgAdmin && (
                        <div className={`flex justify-end border-t pt-4 ${s.dark ? "border-slate-700" : "border-slate-200"}`}>
                            <button type="button" className={s.btnDanger} disabled={busy}
                                    onClick={() => window.confirm("Remove this student from your organization? Their program enrollments end and any license is removed.")
                                        && run(async () => { await api.delete(`/org/${orgId}/students/${clientId}`); onClose(); })}>
                                Remove from organization
                            </button>
                        </div>
                    )}
                </div>
            )}
        </OrgModal>
    );
}

function NotShared() {
    const s = useOrgStyles();
    return (
        <p className={`flex items-center gap-2 text-sm ${s.muted}`}>
            <Lock size={14} /> Not shared with your organization.
        </p>
    );
}

function SharedSections({ data, onOpenAnalysis }) {
    const s = useOrgStyles();
    if (!data.detailsAvailable) {
        return (
            <div className={`flex items-start gap-3 rounded-xl border p-4 text-sm ${s.subPanel}`}>
                <Info size={16} className={`mt-0.5 flex-shrink-0 ${s.muted}`} />
                <p className={s.body}>Student details (resume reviews, quarterly results, program progress) are available to Enterprise organizations when the student chooses to share them.</p>
            </div>
        );
    }
    return (
        <div className="space-y-6">
            <section>
                <h4 className={`mb-2 flex items-center gap-2 text-sm font-bold ${s.text}`}><FileText size={16} /> Resume reviews</h4>
                {!data.sharing.shareResume ? <NotShared /> : data.resume.analyses.length === 0 ? (
                    <p className={`text-sm ${s.muted}`}>No resume reviews yet.</p>
                ) : (
                    <ul className={`divide-y rounded-xl border ${s.divider} ${s.dark ? "border-slate-700" : "border-slate-200"}`}>
                        {data.resume.analyses.map((a) => (
                            <li key={a.AnalysisID}>
                                <button type="button" onClick={() => onOpenAnalysis(a.AnalysisID)}
                                        className={`flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm ${s.dark ? "hover:bg-slate-700/40" : "hover:bg-slate-50"}`}>
                                    <span className="min-w-0">
                                        <span className={`block truncate font-semibold ${s.text}`}>{a.Label || a.TargetJob || a.ResumeLabel}</span>
                                        <span className={`block text-xs ${s.muted}`}>
                                            {formatDate(a.CreatedOn)}{a.ProgramName ? ` · ${a.ProgramName} criteria` : ""}
                                        </span>
                                    </span>
                                    {a.OverallScore != null && <Pill tone={a.OverallScore >= 80 ? "success" : a.OverallScore >= 60 ? "warning" : "danger"}>{a.OverallScore}</Pill>}
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            <section>
                <h4 className={`mb-2 flex items-center gap-2 text-sm font-bold ${s.text}`}><CalendarRange size={16} /> Quarterly results</h4>
                {!data.sharing.shareQuarterlyResults ? <NotShared /> : (
                    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                        {data.quarterlyResults.filter((q) => q.totalTasks > 0).length === 0
                            ? <p className={`col-span-full text-sm ${s.muted}`}>No quarterly goals yet.</p>
                            : data.quarterlyResults.filter((q) => q.totalTasks > 0).map((q) => (
                                <div key={q.QuarterID} className={`rounded-xl border p-3 ${s.subPanel}`}>
                                    <div className={`text-xs ${s.muted}`}>{q.QuarterName} {q.FiscalYear}</div>
                                    <div className={`text-lg font-bold tabular-nums ${s.text}`}>{q.completionRate}%</div>
                                    <div className={`text-xs ${s.muted}`}>{q.completedTasks} of {q.totalTasks} goals</div>
                                </div>
                            ))}
                    </div>
                )}
            </section>

            <section>
                <h4 className={`mb-2 flex items-center gap-2 text-sm font-bold ${s.text}`}><ClipboardCheck size={16} /> Program progress</h4>
                {!data.sharing.shareProgramProgress ? <NotShared /> : data.programProgress.length === 0 ? (
                    <p className={`text-sm ${s.muted}`}>Not in a program.</p>
                ) : (
                    <div className="space-y-3">
                        {data.programProgress.map((p) => {
                            const program = data.programs.find((x) => x.ProgramID === p.ProgramID);
                            return (
                                <div key={p.ProgramID} className={`rounded-xl border p-4 ${s.subPanel}`}>
                                    <div className={`mb-2 font-semibold ${s.text}`}>{program?.ProgramName}</div>
                                    <div className={`grid gap-2 text-sm sm:grid-cols-3 ${s.body}`}>
                                        <span>Assessments: <strong>{p.assessmentsCompleted}/{p.assessmentsTotal}</strong></span>
                                        <span>Resume reviews: <strong>{p.resumeEvaluations?.Count || 0}</strong>{p.resumeEvaluations?.BestScore != null && ` (best ${p.resumeEvaluations.BestScore})`}</span>
                                        <span>Mock interviews: <strong>{p.interviewSessions?.Count || 0}</strong></span>
                                    </div>
                                    {p.assessments.length > 0 && (
                                        <ul className="mt-2 flex flex-wrap gap-1">
                                            {p.assessments.map((a) => <Pill key={a.SurveyID} tone={a.Completed ? "success" : "neutral"}>{a.Title}</Pill>)}
                                        </ul>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>
        </div>
    );
}

function AnalysisView({ analysis, onBack }) {
    const s = useOrgStyles();
    const r = analysis.results || {};
    return (
        <div className="space-y-4">
            <button type="button" className={s.btnLink} onClick={onBack}>← Back to student</button>
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                    <div className={`font-bold ${s.text}`}>{analysis.Label || r.targetJob || "Resume review"}</div>
                    <div className={`text-xs ${s.muted}`}>{formatDate(analysis.CreatedOn)}{r.targetJob ? ` · ${r.targetJob}` : ""}</div>
                </div>
                {r.overallScore != null && <div className={`text-3xl font-extrabold tabular-nums ${s.text}`}>{r.overallScore}<span className={`text-base ${s.muted}`}>/100</span></div>}
            </div>
            {r.summary && <p className={`text-sm ${s.body}`}>{r.summary}</p>}
            <ul className="space-y-3">
                {(r.categories || []).map((c) => (
                    <li key={c.name} className={`rounded-xl border p-3 ${s.subPanel}`}>
                        <div className="flex items-center justify-between gap-2 text-sm">
                            <span className={`font-semibold ${s.text}`}>{c.name}</span>
                            <span className={`tabular-nums ${s.body}`}>{c.score}{c.grade ? ` · ${c.grade}` : ""}</span>
                        </div>
                        {c.justification && <p className={`mt-1 text-xs ${s.muted}`}>{c.justification}</p>}
                    </li>
                ))}
            </ul>
            {r.topRecommendation && (
                <p className={`rounded-xl border p-3 text-sm ${s.subPanel} ${s.body}`}><strong>Top recommendation: </strong>{r.topRecommendation}</p>
            )}
        </div>
    );
}
